import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  CalendarHeart,
  Check,
  Dna,
  Dog,
  DownloadCloud,
  FileSignature,
  GitBranch,
  HeartPulse,
  KeyRound,
  PawPrint,
  Smartphone,
  Users,
} from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { PublicLayout } from '../components/public/PublicLayout';
import { HeroPreview } from '../components/public/HeroPreview';
import { SectionHeader } from '../components/public/SectionHeader';

const FEATURES = [
  { key: 'dogs', icon: Dog },
  { key: 'breeding', icon: CalendarHeart },
  { key: 'pregnancy', icon: HeartPulse },
  { key: 'litters', icon: PawPrint },
  { key: 'prospects', icon: FileSignature },
  { key: 'pedigree', icon: GitBranch },
] as const;

const WORKFLOW_STEPS = [
  { key: 'heat', icon: CalendarHeart },
  { key: 'mating', icon: Dna },
  { key: 'pregnancy', icon: HeartPulse },
  { key: 'litter', icon: PawPrint },
] as const;

const TRUST_ITEMS = [
  { key: 'twoFactor', icon: KeyRound },
  { key: 'export', icon: DownloadCloud },
  { key: 'team', icon: Users },
  { key: 'devices', icon: Smartphone },
] as const;

/** Listen-Uebersetzungen (returnObjects); fehlt ein Eintrag in einer Sprache, liefert i18next den
 * Schluessel als String zurueck - dann lieber nichts anzeigen, als die Startseite abstuerzen zu lassen. */
const asList = (value: unknown): string[] => (Array.isArray(value) ? value : []);

const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hash, key } = useLocation();

  useSeo({ title: t('landing:meta.title'), description: t('landing:meta.description'), path: '/', lang: i18n.resolvedLanguage });

  // Kopf-/Fusszeile verlinken per Router auf "/#abschnitt" - der Router scrollt dabei nicht von
  // selbst. location.key im Effekt, damit auch ein zweiter Klick auf denselben Link wieder springt.
  // Ohne Hash (z.B. Klick aufs Logo, Rueckkehr von der Paketseite) wieder oben beginnen.
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, key]);

  const heroPoints = asList(t('landing:hero.points', { returnObjects: true }));

  return (
    <PublicLayout>
      {({ openRegister }) => (
        <>
          {/* Hero */}
          <section className="relative -mt-16 overflow-hidden pt-16 lg:-mt-[4.5rem] lg:pt-[4.5rem]">
            <div aria-hidden="true" className="landing-grid pointer-events-none absolute inset-0" />
            <div
              aria-hidden="true"
              className="deco-glow pointer-events-none absolute left-1/2 top-0 h-[36rem] w-[64rem] -translate-x-1/2 -translate-y-1/3 rounded-full bg-primary-500/15 blur-3xl"
            />

            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-12 sm:px-6 sm:pt-16 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:px-8 lg:pb-28 lg:pt-20 xl:gap-20">
              <div className="text-center lg:text-left">
                <span className="inline-flex items-center gap-2 rounded-full border border-primary-400/25 bg-primary-500/10 px-3.5 py-1 text-xs font-medium text-primary-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-400" />
                  {t('landing:hero.badge')}
                </span>

                <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-dark-50 sm:text-5xl xl:text-6xl">
                  {t('landing:hero.titleLine1')}{' '}
                  <span className="text-gradient">{t('landing:hero.titleHighlight')}</span>
                </h1>

                <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-dark-300 lg:mx-0">
                  {t('landing:hero.subtitle')}
                </p>

                <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
                  <button onClick={openRegister} className="btn btn-primary btn-lg w-full sm:w-auto">
                    {t('landing:hero.ctaStart')}
                    <ArrowRight size={20} />
                  </button>
                  <Link to={{ hash: '#funktionen' }} className="btn btn-outline btn-lg w-full sm:w-auto">
                    {t('landing:hero.ctaFeatures')}
                  </Link>
                </div>

                <ul className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-dark-400 lg:justify-start">
                  {heroPoints.map((point) => (
                    <li key={point} className="inline-flex items-center gap-2">
                      <Check size={16} className="text-primary-400" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              <HeroPreview />
            </div>
          </section>

          {/* Funktionen */}
          <section id="funktionen" className="scroll-mt-20 border-t border-dark-700/50 py-20 sm:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <SectionHeader
                eyebrow={t('landing:features.eyebrow')}
                title={t('landing:features.title')}
                subtitle={t('landing:features.subtitle')}
              />

              <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {FEATURES.map(({ key: featureKey, icon: Icon }) => {
                  const points = asList(t(`landing:features.items.${featureKey}.points`, { returnObjects: true }));
                  return (
                    <article key={featureKey} className="card flex flex-col transition-colors hover:border-primary-400/40">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-primary-400/20 bg-primary-500/10 text-primary-300">
                        <Icon size={22} />
                      </span>
                      <h3 className="mt-5 text-lg font-semibold text-dark-50">
                        {t(`landing:features.items.${featureKey}.title`)}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-dark-400">
                        {t(`landing:features.items.${featureKey}.description`)}
                      </p>
                      <ul className="mt-5 space-y-2.5 border-t border-dark-700/60 pt-5">
                        {points.map((point) => (
                          <li key={point} className="flex items-start gap-2.5 text-sm text-dark-200">
                            <Check size={16} className="mt-0.5 shrink-0 text-primary-400" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </article>
                  );
                })}
              </div>

              <div className="mt-10 text-center">
                <Link
                  to="/pakete"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary-300 transition-colors hover:text-primary-200"
                >
                  {t('landing:features.more')}
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </section>

          {/* Ablauf */}
          <section id="ablauf" className="scroll-mt-20 border-t border-dark-700/50 bg-dark-950/25 py-20 sm:py-28">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <SectionHeader
                eyebrow={t('landing:workflow.eyebrow')}
                title={t('landing:workflow.title')}
                subtitle={t('landing:workflow.subtitle')}
              />

              <ol className="relative mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                <div
                  aria-hidden="true"
                  className="absolute left-6 right-[calc(25%-3rem)] top-6 hidden h-px bg-gradient-to-r from-primary-500/50 via-dark-600 to-secondary-500/50 lg:block"
                />
                {WORKFLOW_STEPS.map(({ key: stepKey, icon: Icon }, index) => (
                  <li key={stepKey} className="relative">
                    <span className="relative flex h-12 w-12 items-center justify-center rounded-full border border-dark-600 bg-dark-900 text-primary-300 shadow-lg">
                      <Icon size={20} />
                    </span>
                    <p className="mt-6 text-xs font-semibold tracking-[0.2em] text-primary-300">
                      {String(index + 1).padStart(2, '0')}
                    </p>
                    <h3 className="mt-1.5 text-lg font-semibold text-dark-50">
                      {t(`landing:workflow.steps.${stepKey}.title`)}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-dark-400">
                      {t(`landing:workflow.steps.${stepKey}.description`)}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Sicherheit & Zusammenarbeit */}
          <section id="sicherheit" className="scroll-mt-20 border-t border-dark-700/50 py-20 sm:py-28">
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.15fr] lg:gap-16 lg:px-8">
              <SectionHeader
                align="left"
                eyebrow={t('landing:trust.eyebrow')}
                title={t('landing:trust.title')}
                subtitle={t('landing:trust.subtitle')}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                {TRUST_ITEMS.map(({ key: itemKey, icon: Icon }) => (
                  <div key={itemKey} className="card">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-500/10 text-secondary-300">
                      <Icon size={20} />
                    </span>
                    <h3 className="mt-4 font-semibold text-dark-50">{t(`landing:trust.items.${itemKey}.title`)}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-dark-400">
                      {t(`landing:trust.items.${itemKey}.description`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Abschluss */}
          <section className="px-4 pb-24 sm:px-6 lg:px-8">
            <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl border border-primary-400/20 bg-gradient-to-br from-primary-600/25 via-dark-800/60 to-secondary-600/20 px-6 py-16 text-center sm:px-12 sm:py-20">
              <div aria-hidden="true" className="landing-grid pointer-events-none absolute inset-0" />
              <div className="relative mx-auto max-w-3xl">
                <h2 className="text-balance text-3xl font-bold tracking-tight text-dark-50 sm:text-4xl">{t('landing:cta.title')}</h2>
                <p className="mt-4 text-base leading-relaxed text-dark-300 sm:text-lg">{t('landing:cta.subtitle')}</p>
                <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                  <button onClick={openRegister} className="btn btn-primary btn-lg w-full sm:w-auto">
                    {t('landing:cta.button')}
                    <ArrowRight size={20} />
                  </button>
                  <Link to="/pakete" className="btn btn-outline btn-lg w-full sm:w-auto">
                    {t('landing:cta.secondary')}
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </PublicLayout>
  );
};

export default LandingPage;
